import React, { useEffect, useState, useMemo } from "react";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Toast } from "primereact/toast";

import { FaEye, FaEdit, FaPlus, FaTrash } from "react-icons/fa";
import "../styles/merchants.css";
import { deleteUserByID, fetchAllUsers } from "../api/api";
import { useNavigate } from "react-router-dom";

const Users = ({ isSidebarOpen }) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [users, setAllUsers] = useState([]);
  const toast = React.useRef(null);
  useEffect(() => {
    const fetchData = async () => {
      try {
        const res = await fetchAllUsers();
        if (res?.users) {
          console.log("Fetched users:", res.users);
          setAllUsers(res.users);
        }
      } catch (err) {
        console.error("Error fetching users:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);
  const refreshUsers = async () => {
    const res = await fetchAllUsers();
    if (res?.users) setAllUsers(res.users);
  };
  // ✅ Columns definition
  const columns = useMemo(
    () => [
      { field: "id", header: "ID", style: { width: "60px" } },
      { field: "username", header: "User Name", style: { width: "150px" } },
      { field: "name", header: "Name", style: { width: "180px" } },
      { field: "user_type", header: "User Type", style: { width: "120px" } },
    ],
    []
  );
  // ✅ Delete user handler
  const handleDeleteUser = async (userId) => {
    try {
      const res = await deleteUserByID(userId);
      if (res?.isSuccess) {
        toast.current.show({
          severity: "success",
          summary: "Success",
          detail: "User deleted successfully",
          life: 2000,
        });
        refreshUsers();
      } else {
        toast.current.show({
          severity: "error",
          summary: "Error",
          detail: res?.message || "Failed to delete user",
          life: 3000,
        });
      }
    } catch (err) {
      console.error("Error deleting user:", err);
      toast.current.show({
        severity: "error",
        summary: "Error",
        detail: "Something went wrong while deleting user",
        life: 3000,
      });
    }
  };

  // ✅ Actions column
  const actionBodyTemplate = (rowData) => {
    if (rowData.user_type?.toLowerCase() === "admin") return null;

    return (
      <div style={{ display: "flex", gap: "10px", justifyContent: "center" }}>
        <FaEye
          className="view-icon"
          title="View"
          onClick={() =>
            navigate("/dashboard/user?mode=view", {
              state: { user: rowData, mode: "view" },
            })
          }
        />
        <FaEdit
          className="view-icon"
          title="Edit"
          onClick={() =>
            navigate("/dashboard/user?mode=edit", {
              state: { user: rowData, mode: "edit" },
            })
          }
        />
        <FaTrash
          className="delete-icon"
          title="Delete"
          onClick={() => {
            const confirmed = window.confirm(
              `Are you sure you want to delete user "${rowData.username}"?`
            );
            if (confirmed) {
              handleDeleteUser(rowData.id);
            }
          }}
        />
      </div>
    );
  };
  const rolesBodyTemplate = (rowData) => {
    const roles = rowData.roles || [];
    if (!roles.length) return "-";

    return (
      <ul style={{ margin: 0, paddingLeft: "18px" }}>
        {roles.map((role, index) => (
          <li key={index} style={{ listStyleType: "disc" }}>
            {role === "all"
              ? "All Permissions"
              : role
                  .replace(/_/g, " ")
                  .replace(/\b\w/g, (c) => c.toUpperCase())}
          </li>
        ))}
      </ul>
    );
  };

   if (loading) {
    return (
      <div className={`db-container ${isSidebarOpen ? "shifted" : ""}`}>
        <div className="db-loader"><div className="loader-spinner" /><p>Loading User data…</p></div>
      </div>
    );
  }

  return (
    <div className={`reporting-container ${isSidebarOpen ? "shifted" : ""}`}>
     
      <Toast ref={toast} />
      {/* ✅ Add User Button */}
      <div className="add-merchant-container">
        <button
          className="add-merchant-btn"
          onClick={() => navigate("/dashboard/user?mode=create")}
        >
          <FaPlus />
        </button>
      </div>

      {/* ✅ Table Section */}
      <section className="reporting-table-section">
        <DataTable
          value={users}
          dataKey="id"
          className="dashboard-table"
          responsiveLayout="scroll"
          paginator
          rows={10}
          paginatorClassName="custom-paginator"
        >
          {columns.map((col) => (
            <Column
              key={col.field}
              field={col.field}
              header={col.header}
              style={col.style}
            />
          ))}
          {/* ✅ Roles column */}
          <Column
            field="roles"
            header="Assigned Roles"
            body={rolesBodyTemplate}
            style={{ width: "250px" }}
          />

          {/* ✅ Actions column — hidden for admin */}
          <Column
            header="Actions"
            body={actionBodyTemplate}
            style={{ width: "120px", textAlign: "center" }}
          />
        </DataTable>
      </section>
    </div>
  );
};

export default Users;
